import React from 'react';
import { Box, CircularProgress, Typography, Button, Avatar, Slide } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const loadingStates = {
  initial: {
    message: 'Authenticating...',
    subMessage: 'Please wait while we verify your credentials'
  },
  checkingTrust: {
    message: 'Checking device trust status...',
    subMessage: 'Verifying if this is a trusted device'
  },
  verifyingMfa: {
    message: 'Verifying MFA...',
    subMessage: 'Completing secure authentication'
  },
  finalizing: {
    message: 'Almost there...',
    subMessage: 'Finalizing your login'
  },
  loading: {
    message: 'Loading...',
    subMessage: 'Please wait while we set things up'
  },
  verifying: {
    message: 'Verifying...',
    subMessage: 'Checking your credentials'
  },
  success: {
    message: 'Success!',
    subMessage: 'You have been authenticated successfully'
  },
  error: {
    message: 'Authentication Failed',
    subMessage: 'There was a problem with your login'
  },
  accountNotFound: {
    message: 'Account Not Found',
    subMessage: 'No account exists with this email address'
  },
  invalidCredentials: {
    message: 'Invalid Credentials',
    subMessage: 'The email or password you entered is incorrect'
  },
  notFound: {
    message: 'Not Found',
    subMessage: 'The requested resource could not be found'
  }
};

const AuthLoadingState = ({ 
  state = 'initial', 
  onReset, 
  customMessage, 
  customSubMessage 
}) => {
  const theme = useTheme();
  const stateData = loadingStates[state] || loadingStates.initial;
  
  const message = customMessage || stateData.message;
  const subMessage = customSubMessage || stateData.subMessage;
  
  const isLoading = ['initial', 'loading', 'verifying', 'checkingTrust', 'verifyingMfa', 'finalizing'].includes(state);
  const isSuccess = state === 'success';
  const isError = ['error', 'notFound', 'accountNotFound', 'invalidCredentials'].includes(state);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        p: 2
      }}
    >
      <Slide direction="down" in={true} timeout={{ enter: 700, exit: 500 }} easing={{ enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
        <Avatar
          sx={{
            width: 70,
            height: 70,
            mb: 3,
            bgcolor: isSuccess 
              ? theme.palette.success.main 
              : isError 
                ? theme.palette.error.main 
                : theme.palette.primary.main,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            animation: isError ? 'shake 0.5s cubic-bezier(.36,.07,.19,.97) both' : 'none',
            '@keyframes shake': {
              '10%, 90%': {
                transform: 'translate3d(-1px, 0, 0)',
              },
              '20%, 80%': {
                transform: 'translate3d(2px, 0, 0)',
              },
              '30%, 50%, 70%': {
                transform: 'translate3d(-4px, 0, 0)',
              },
              '40%, 60%': {
                transform: 'translate3d(4px, 0, 0)',
              }
            }
          }}
        >
          {isLoading && (
            <CircularProgress 
              size={40} 
              thickness={4}
              sx={{ 
                color: '#fff'
              }} 
            />
          )}
          {isSuccess && <CheckCircleIcon sx={{ fontSize: 40 }} />}
          {isError && <ErrorOutlineIcon sx={{ fontSize: 40 }} />}
        </Avatar>
      </Slide>
      
      <Slide direction="up" in={true} timeout={{ enter: 800, exit: 500 }} easing={{ enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            fontWeight: 600,
            color: isError 
              ? theme.palette.error.main
              : theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
            mb: 1,
            textAlign: 'center'
          }}
        >
          {message}
        </Typography>
      </Slide>
      
      <Slide direction="up" in={true} timeout={{ enter: 900, exit: 500 }} easing={{ enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 3,
            color: isError
              ? alpha(theme.palette.error.main, 0.8)
              : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
            textAlign: 'center',
            maxWidth: '80%'
          }}
        >
          {subMessage}
        </Typography>
      </Slide>
      
      {isError && onReset && (
        <Slide direction="up" in={true} timeout={{ enter: 1000, exit: 500 }} easing={{ enter: 'cubic-bezier(0.34, 1.56, 0.64, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <Button
            variant="contained"
            onClick={onReset}
            color="error"
            sx={{
              mt: 2,
              py: 1,
              px: 3,
              borderRadius: 3,
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            Try Again
          </Button>
        </Slide>
      )}
    </Box>
  );
};

export default AuthLoadingState; 